import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { ProviderService } from './services/provider.service';
import { providerBasePath, routeTypeFor } from './shared/provider-routing';
import { Provider } from './models/provider.model';
import { discoveryStateColor, discoveryStateKey } from './shared/provider-discovery';
import { Grid } from '../../shared/components/grid/grid';
import { GridPage } from '../../shared/components/grid/grid-page';
import {
  GridColumn,
  GridRowAction,
  GridSort,
  GridStatus,
  gridColumns,
} from '../../shared/components/grid/models/grid.model';
import { ToastService } from '../../shared/services/toast-service';
import { ModalService } from '../../shared/components/modal/modal.service';
import { LanguageService } from '../../core/i18n/language.service';

@Component({
  selector: 'app-providers',
  standalone: true,
  imports: [Grid, GridPage, TranslocoDirective],
  templateUrl: './providers.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProvidersComponent implements OnInit {
  private readonly service = inject(ProviderService);
  private readonly toastService = inject(ToastService);
  private readonly modal = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly router = inject(Router);

  readonly providers = signal<Provider[]>([]);
  readonly page = signal(0);
  readonly size = signal(20);
  readonly totalElements = signal(0);
  readonly totalPages = signal(0);
  readonly sort = signal<GridSort>({ sortBy: 'createdAt', sortDirection: 'DESC' });
  readonly isLoading = signal(false);

  /** Stable row id for tracking. */
  readonly providerId = (p: Provider) => p.id;
  /** Narrows the erased-generic row that grid actions hand back. */
  readonly asProvider = (row: unknown): Provider => row as Provider;

  /** Columns as a computed so labels re-translate on a language switch. */
  readonly columns = computed<GridColumn<Provider>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`providers.columns.${k}`);
    return gridColumns<Provider>([
      { key: 'name', label: t('name'), type: 'text', textVariant: 'strong', sortable: true, alwaysVisible: true },
      { key: 'type', label: t('type'), type: 'text', sortable: false, width: 140 },
      { key: 'discoveryState', label: t('discovery'), type: 'status', sortable: false, width: 160, status: (_v, row) => this.discoveryStatus(row) },
      { key: 'providerVersion', label: t('version'), type: 'text', textVariant: 'muted', sortable: false, width: 160, value: (row) => row.providerVersion ?? '—' },
      { key: 'serviceUrl', label: t('serviceUrl'), type: 'text', sortable: false, width: 280, hiddenByDefault: true },
      { key: 'enabled', label: t('enabled'), type: 'boolean', sortable: true, width: 110 },
      { key: 'createdAt', label: t('created'), type: 'date', sortable: true, width: 180 },
    ]);
  });

  /** Row overflow-menu actions — replaces the old right-click options menu. */
  readonly rowActions = computed<GridRowAction<Provider>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`providers.actions.${k}`);
    return [
      // Empty (not absent) section: distinct from the ungrouped danger actions
      // below, so the overflow menu still draws a divider between them without
      // rendering a group heading — same effect as the tenant table's grouping.
      { id: 'view', label: t('view'), icon: 'icon-view-eye', placement: 'menu', section: '' },
      { id: 'enable', label: t('enable'), icon: 'icon-checkmark', placement: 'menu', section: '', disabled: (row) => row.enabled },
      { id: 'disable', label: t('disable'), icon: 'icon-disable-ban', placement: 'menu', danger: true, disabled: (row) => !row.enabled },
      { id: 'delete', label: t('delete'), icon: 'icon-delete-trash', placement: 'menu', danger: true },
    ];
  });

  ngOnInit(): void {
    this.fetch();
  }

  fetch(): void {
    this.isLoading.set(true);
    this.service
      .getAll(this.page(), this.size())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.providers.set(res.data);
          // Backend list is a plain array (no total); infer whether a next page
          // exists so pagination stays sane (one extra page while full).
          const totalPages = res.data.length === this.size() ? this.page() + 2 : this.page() + 1;
          this.totalPages.set(totalPages);
          this.totalElements.set(this.page() * this.size() + res.data.length);
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.transloco.translate('providers.toast.loadFailed'), 'error');
          this.isLoading.set(false);
        },
      });
  }

  onSortChange(sort: GridSort): void {
    this.sort.set(sort);
    this.page.set(0);
    this.fetch();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.fetch();
  }

  onPageSizeChange(size: number): void {
    this.size.set(size);
    this.page.set(0);
    this.fetch();
  }

  onRowAction(actionId: string, provider: Provider): void {
    if (actionId === 'view') {
      this.viewProvider(provider);
    } else if (actionId === 'enable') {
      this.setEnabled(provider, true);
    } else if (actionId === 'disable') {
      void this.confirmDisable(provider);
    } else if (actionId === 'delete') {
      void this.confirmDelete(provider);
    }
  }

  /** Double-click opens the provider detail page (mirrors the device table). */
  onRowDblClick(provider: Provider): void {
    this.viewProvider(provider);
  }

  private viewProvider(provider: Provider): void {
    void this.router.navigate(providerBasePath(routeTypeFor(provider.type), provider.id));
  }

  /** Discovery state pill; the failure reason is on the provider's Details tab. */
  private discoveryStatus(provider: Provider): GridStatus {
    return {
      label: this.transloco.translate(discoveryStateKey(provider.discoveryState)),
      color: discoveryStateColor(provider.discoveryState) ?? 'neutral',
      hollow: provider.discoveryState !== 'READY',
    };
  }

  private async confirmDelete(provider: Provider): Promise<void> {
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.transloco.translate('providers.delete.title'),
      message: this.transloco.translate('providers.delete.message', { name: provider.name }),
      confirmLabel: this.transloco.translate('common.delete'),
      confirmWord: provider.name,
    });
    if (confirmed) this.deleteProvider(provider);
  }

  private async confirmDisable(provider: Provider): Promise<void> {
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.transloco.translate('providers.detailPage.disable.title'),
      message: this.transloco.translate('providers.detailPage.disable.message', { name: provider.name }),
      confirmLabel: this.transloco.translate('providers.detailPage.disable.confirm'),
      confirmWord: provider.name,
    });
    if (confirmed) this.setEnabled(provider, false);
  }

  private setEnabled(provider: Provider, enabled: boolean): void {
    this.service
      .update(provider.id, { enabled })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('providers.toast.updated'), 'success');
          this.fetch();
        },
        error: () => this.toastService.show(this.transloco.translate('providers.toast.updateFailed'), 'error'),
      });
  }

  private deleteProvider(provider: Provider): void {
    this.service
      .delete(provider.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('providers.toast.deleted'), 'success');
          this.fetch();
        },
        error: () => this.toastService.show(this.transloco.translate('providers.toast.deleteFailed'), 'error'),
      });
  }
}
